import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static get targets() {
    return [
      "industrySelect",
      "industryTagTemplate",
      "industryTagsList",
      "locationSelect",
      "locationTagTemplate",
      "locationTagsList",
      "fieldLoader",
      "discoverIntentCountFrame",
      "continueButton",
    ]
  }

  connect() {
    this.#setUpSelectizeElements()
    this.discoverIntentCountFrameTarget.addEventListener("turbo:frame-render", (e) => {
      e.detail.fetchResponse.response.text().then((html) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const frameElement = doc.querySelector("turbo-frame")
        const count = Number(frameElement.dataset.count)

        if (count > 0) {
          this.#enableContinueButton()
        } else {
          this.#disableContinueButton()
        }
      })
    })
  }

  // Clean up selectize
  disconnect() {
    this.industrySelectize?.destroy()
    this.locationSelectize?.destroy()
  }

  removeIndustryTag(event) {
    this.#removeTag(this.industrySelectize, this.industryTagsListTarget, event.params.selectValue)
  }

  removeLocationTag(event) {
    const selectizeValue = `${event.params.selectType}-${event.params.selectValue}`
    this.#removeTag(this.locationSelectize, this.locationTagsListTarget, event.params.selectValue, selectizeValue)
  }

  #addTag(container, tagTemplate, value, label, type) {
    let tagHtml = tagTemplate.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, label)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    if (type) {
      tagHtml = tagHtml.replace(/%TYPE%/g, type)
    }
    let tagNode = document.createElement("div")
    tagNode.innerHTML = tagHtml

    container.appendChild(tagNode.firstElementChild)
  }

  #removeTag(selectize, container, tagValue, selectizeValue) {
    const tag = container.querySelector(`[data-value="${tagValue}"]`)
    tag.remove()
    selectize.removeItem(selectizeValue || tagValue)
    this.#updateDiscoverIntentCount()
  }

  #setUpSelectizeElements() {
    this.industrySelectize = $(this.industrySelectTarget).selectize({
      create: false,
      persist: false,
      copyClassesToDropdown: false,
      sortField: "text",
      lockOptgroupOrder: true,
      onItemAdd: (value, $item) => {
        const label = $item[0].innerText
        this.#addTag(this.industryTagsListTarget, this.industryTagTemplateTarget, value, label)
        this.#updateDiscoverIntentCount()
        this.#refreshSelectize(this.industrySelectize)
      },
      render: {
        item: (item) => {
          return `<div value="${item.value}" style='display: none;' >${item.text}</div>`
        }, // Prevent rendering item in input
      },
    })[0].selectize

    this.runningRequests = 0
    this.locationSelectize = $(this.locationSelectTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
      optgroupField: "type",
      optgroups: [
        { value: "continent", label: "Continent" },
        { value: "business_region", label: "Business region" },
        { value: "country", label: "Country" },
        { value: "state", label: "State" },
        { value: "city", label: "City" },
      ],
      persist: false,
      create: false,
      copyClassesToDropdown: false,
      sortField: [{ field: "label" }],
      render: {
        item: (item) => {
          return `<div value="${item.value}" style='display: none;' >${item.text}</div>`
        }, // Prevent rendering item in input
      },
      load: (query, callback) => {
        if (!query.length) {
          return callback()
        }

        // Show the spinner
        this.runningRequests += 1
        this.fieldLoaderTarget.classList.remove("d-none")

        fetch(`/locations?query=${encodeURIComponent(query)}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.json()
          })
          .then((data) => {
            data = data.map((item) => ({
              ...item,
              value: `${item.type}-${item.value}`,
            }))

            callback(data)
          })
          .catch((error) => {
            callback()
            console.error(error)
          })
          .finally(() => {
            this.runningRequests -= 1

            if (this.runningRequests <= 0) {
              // Hide the spinner, but only if there are no pending requests
              this.fieldLoaderTarget.classList.add("d-none")
            }
          })
      },
      onItemAdd: (compositeValue) => {
        // Extract the original type and value from the composite value
        const [, type, value] = compositeValue.match(/^([^-]+)-(.+)$/)
        const option = this.locationSelectize.options[compositeValue]
        const label = option.label
        this.#addTag(this.locationTagsListTarget, this.locationTagTemplateTarget, value, label, type)
        this.#updateDiscoverIntentCount()
        this.#refreshSelectize(this.locationSelectize)
      },
    })[0].selectize
  }

  #updateDiscoverIntentCount() {
    const filters = {}
    if (this.industryTagsListTarget.children.length > 0) {
      const industryValues = Array.from(this.industryTagsListTarget.children).map((tag) => tag.dataset.value)
      filters["industry_included"] = industryValues.join("|")
    }

    const countryFilters = {}
    Array.from(this.locationTagsListTarget.children).forEach((tag) => {
      countryFilters[tag.dataset.type] ||= []
      countryFilters[tag.dataset.type].push(tag.dataset.value)
    })

    Object.keys(countryFilters).forEach((type) => {
      filters[`location_${type}_included`] = countryFilters[type].join("|")
    })

    if (Object.keys(filters).length > 0) {
      const discoverUrl = `/discover?${new URLSearchParams(filters).toString()}`
      this.continueButtonTarget.href = this.#addSignupCompletionParam(discoverUrl)
      filters.onboarding = true
      this.discoverIntentCountFrameTarget.src = `/discover?${new URLSearchParams(filters).toString()}`
      this.discoverIntentCountFrameTarget.classList.remove("d-none")
    } else {
      this.continueButtonTarget.href = "#"
      this.discoverIntentCountFrameTarget.classList.add("d-none")
      this.#disableContinueButton()
    }
  }

  #refreshSelectize(selectize) {
    selectize.refreshOptions(false)
    selectize.clear()
    selectize.close()
  }

  #enableContinueButton() {
    this.continueButtonTarget.disabled = false
    this.continueButtonTarget.classList.remove("disabled")
  }

  #disableContinueButton() {
    this.continueButtonTarget.disabled = true
    this.continueButtonTarget.classList.add("disabled")
  }

  #addSignupCompletionParam(url) {
    const separator = url.includes("?") ? "&" : "?"
    return `${url}${separator}sign_up_completed=true`
  }
}
